// Shared invoice product helpers — used by InvoiceForm.js (create + draft edit),
// InvoiceItem.js (detail page) and the containers that talk to the details
// endpoint, so the two product shapes are read and written consistently.
//
// Two shapes exist:
//   local  (accumulated in InvoiceForm before the invoice exists)
//          { sale_type, brand, group, category, product_code, quantity }
//          where each level is the full option object from the cascade.
//   server (an invoice_detail returned under `invoice.details`)
//          { id, categories: [saleType, brand?, group?, category?], product, category, quantity }
//          where `categories` holds the client category ancestry as plain names.

function isServerProduct(product) {
  return Boolean(product && (product.categories || product.product))
}

// `categories` is the ancestry of `category`, root first. Index 0 is the modal's
// "Tipo de venda" — the root client product category, which is what the cascade
// passes as `parentId` to get the Marca list — followed by Marca, Grupo and
// Categoria. The product tables render indexes 1..3 only, mirroring
// DefaultTemplate's `filter((e, i) => i !== 0)`. The hierarchy is non-strict, so
// a shorter ancestry means the product hangs off that level directly.
function serverCategoryNames(product) {
  return Array.isArray(product.categories) ? product.categories : []
}

// Returns the deepest category the user actually selected. The client category
// hierarchy is non-strict — a sale type may have no brands, and a brand no
// groups or categories, beneath it — so the product is attached to whichever
// level is the leaf.
export function deepestCategoryId({ selectedCategory, selectedGroup, selectedBrand, selectedSaleType }) {
  const deepest = selectedCategory || selectedGroup || selectedBrand || selectedSaleType
  return deepest ? deepest.id : null
}

// Normalizes a unit price to a Number, or null when absent/blank — used both
// for the raw string coming out of the popup's "Preço" input and for the
// `unit_price` value received from the backend on a persisted invoice_detail.
export function toUnitPrice(value) {
  if (value === undefined || value === null || value === '') return null
  const num = Number(value)
  return isNaN(num) ? null : num
}

// Flattens either shape into the plain values the tables render, so the same
// cells work for a locally accumulated product and a persisted invoice detail.
export function normalizeInvoiceProduct(product) {
  if (!product) return { brand: null, group: null, category: null, productCode: null, quantity: null }

  if (isServerProduct(product)) {
    const categories = serverCategoryNames(product)
    return {
      brand: categories[1] || null,
      group: categories[2] || null,
      category: categories[3] || null,
      productCode: (product.product && product.product.external_identifier) || null,
      quantity: product.quantity,
    }
  }

  return {
    brand: (product.brand && product.brand.name) || null,
    group: (product.group && product.group.name) || null,
    category: (product.category && product.category.name) || null,
    productCode: (product.product_code && product.product_code.external_identifier) || null,
    quantity: product.quantity,
  }
}

// Builds the initial values for the add/edit product modal from either shape.
// A local product already carries the full cascade option objects; a server
// invoice_detail only carries names for the category ancestry, so every level —
// including the modal's own root ("Tipo de venda") — is handed over as a name
// for the form container to resolve against the option lists it fetches.
export function toProductFormInitialValues(product) {
  if (!product) return null

  if (isServerProduct(product)) {
    const categories = serverCategoryNames(product)
    return {
      saleTypeName: categories[0] || null,
      brandName: categories[1] || null,
      groupName: categories[2] || null,
      categoryName: categories[3] || null,
      selectedProduct: product.product || null,
      quantity: product.quantity,
      unitPrice: toUnitPrice(product.unit_price),
    }
  }

  return {
    selectedSaleType: product.sale_type,
    selectedBrand: product.brand,
    selectedGroup: product.group,
    selectedCategory: product.category,
    selectedProduct: product.product_code,
    quantity: product.quantity,
    unitPrice: toUnitPrice(product.unit_price),
  }
}

// Maps a locally accumulated product onto the arguments
// InvoiceService.addInvoiceProduct expects for POST /invoices/:id/details.
export function toInvoiceDetailPayload(product) {
  return {
    productId: product.product_code && product.product_code.id,
    clientCategoryId: deepestCategoryId({
      selectedCategory: product.category,
      selectedGroup: product.group,
      selectedBrand: product.brand,
      selectedSaleType: product.sale_type,
    }),
    quantity: Number(product.quantity),
    unitPrice: toUnitPrice(product.unit_price),
  }
}
